import React from 'react';
import { Box, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { GridColDef } from '@mui/x-data-grid';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RootState } from '../../../store';
import { FailureReason } from '../../../store/callStats/callStatsTypes';
import ChartPanel from '../components/ChartPanel';
import DataGridTable from '../../Shared/DataGridTable/DataGridTable';
import { CHART_COLORS } from '../components/scoreColors';
import { customError } from '../../../mainTheme';

const REASON_LABEL: Record<FailureReason, string> = {
  no_answer: 'No answer',
  rejected: 'Rejected',
  disconnected: 'Disconnected',
  setup_failed: 'Setup failed',
  unknown: 'Unknown',
};

const rankColumns: GridColDef[] = [
  { field: 'displayName', headerName: 'User', flex: 2, minWidth: 160 },
  {
    field: 'failedCalls',
    headerName: 'Failed',
    type: 'number',
    flex: 1,
    minWidth: 90,
  },
  {
    field: 'totalCalls',
    headerName: 'Total',
    type: 'number',
    flex: 1,
    minWidth: 90,
  },
  {
    field: 'failureRatePct',
    headerName: 'Failure rate',
    type: 'number',
    flex: 1,
    minWidth: 120,
    valueFormatter: ({ value }) => `${value}%`,
  },
];

const FailuresSection: React.FC = () => {
  const { failures } = useSelector((state: RootState) => state.callStats);
  const f = failures.data;

  const reasonData = f
    ? (Object.keys(f.byReason) as FailureReason[]).map((r) => ({
        reason: REASON_LABEL[r],
        count: f.byReason[r],
      }))
    : [];

  const rankRows = (f?.rankedUsers ?? []).map((u) => ({ id: u.userId, ...u }));

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <ChartPanel
          title="Failures by reason"
          subtitle={f ? `${f.totalFailed} failed calls` : undefined}
          loading={failures.loading}
          error={failures.error}
          empty={!failures.loading && (!f || f.totalFailed === 0)}
          mocked={failures.mocked}
          minHeight={300}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={reasonData}
              margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="reason"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-12}
                textAnchor="end"
                height={56}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" name="Failed calls" radius={[4, 4, 0, 0]}>
                {reasonData.map((_, i) => (
                  <Cell
                    key={i}
                    fill={
                      i === 0
                        ? customError
                        : CHART_COLORS[i % CHART_COLORS.length]
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Grid>
      <Grid item xs={12} md={7}>
        <ChartPanel
          title="Users with most failed calls"
          subtitle="Ranked by failed call count"
          loading={failures.loading}
          error={failures.error}
          empty={!failures.loading && rankRows.length === 0}
          mocked={failures.mocked}
          minHeight={360}
        >
          <Box width="100%">
            <DataGridTable
              rows={rankRows}
              columns={rankColumns}
              pageSize={10}
              minHeight="320px"
              maxHeight="360px"
            />
          </Box>
        </ChartPanel>
      </Grid>
    </Grid>
  );
};

export default FailuresSection;
